import React from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  MUD_TYPES,
  PostJobReport,
  PostJobReportRun,
  StepProps,
  calcAvgROP,
  calcTotalInterval,
  createDefaultRun
} from '../types/types';

const Step2DrillingParams: React.FC<StepProps> = ({ reportData, onChange }) => {
  const set = (field: keyof PostJobReport, value: any) =>
    onChange({ [field]: value } as any);

  const totalInterval = calcTotalInterval(
    reportData.urDepthIn,
    reportData.urDepthOut
  );
  const avgROP = calcAvgROP(totalInterval, reportData.totalDrillHours);

  // ── Run helpers ──────────────────────────────────────────────────────────────
  const updateRun = (
    index: number,
    field: keyof PostJobReportRun,
    value: any
  ) => {
    const updated = reportData.runs.map((r, i) =>
      i === index ? { ...r, [field]: value } : r
    );
    onChange({ runs: updated });
  };

  const addRun = () => {
    if (reportData.runs.length >= 5) return;
    onChange({
      runs: [...reportData.runs, createDefaultRun(reportData.runs.length + 1)]
    });
  };

  const removeRun = (index: number) => {
    if (reportData.runs.length <= 1) return;
    onChange({
      runs: reportData.runs
        .filter((_, i) => i !== index)
        .map((r, i) => ({ ...r, runNumber: i + 1 }))
    });
  };

  const numField = (
    index: number,
    field: keyof PostJobReportRun,
    placeholder = '0',
    step = 'any'
  ) => (
    <Form.Control
      size="sm"
      type="number"
      step={step}
      placeholder={placeholder}
      value={reportData.runs[index][field] as any}
      onChange={e =>
        updateRun(
          index,
          field,
          e.target.value === '' ? '' : Number(e.target.value)
        )
      }
      style={{ minWidth: 70 }}
    />
  );

  return (
    <div>
      {/* ── Overall Depths ────────────────────────────────────────────────────── */}
      <Card className="pjr-section-card mb-3">
        <Card.Header className="py-2 fw-semibold fs-9 text-uppercase">
          Depths & Drill Time
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  UR Depth In (ft)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  placeholder="0"
                  value={reportData.urDepthIn}
                  onChange={e =>
                    set(
                      'urDepthIn',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  UR Depth Out (ft)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  placeholder="0"
                  value={reportData.urDepthOut}
                  onChange={e =>
                    set(
                      'urDepthOut',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Total Interval (ft)
                </Form.Label>
                <Form.Control
                  size="sm"
                  className="pjr-calc-field"
                  readOnly
                  value={
                    totalInterval !== null ? totalInterval.toFixed(0) : '—'
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Total Drill Hrs
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.5"
                  placeholder="0"
                  value={reportData.totalDrillHours}
                  onChange={e =>
                    set(
                      'totalDrillHours',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Avg ROP (ft/hr)
                </Form.Label>
                <Form.Control
                  size="sm"
                  className="pjr-calc-field"
                  readOnly
                  value={avgROP !== null ? avgROP.toFixed(1) : '—'}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Mud Type
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={reportData.mudType}
                  onChange={e => set('mudType', e.target.value)}
                >
                  {MUD_TYPES.map(t => (
                    <option key={t}>{t}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Casing Data ───────────────────────────────────────────────────────── */}
      <Card className="pjr-section-card green mb-3">
        <Card.Header className="py-2 fw-semibold fs-9 text-uppercase">
          Casing Data
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="g-3">
            {(
              [
                ['casingSize', 'Casing Size (in)', '0'],
                ['casingWeight', 'Weight (lbs/ft)', '0'],
                ['casingID', 'Casing ID (in)', '0'],
                ['casingSetAt', 'Set At (ft)', '0'],
                ['casingShoeDepth', 'Shoe Depth (ft)', '0']
              ] as [keyof PostJobReport, string, string][]
            ).map(([field, label, ph]) => (
              <Col md={2} key={field}>
                <Form.Group>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    {label}
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.01"
                    placeholder={ph}
                    value={reportData[field] as any}
                    onChange={e =>
                      set(
                        field,
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      {/* ── Run Data ──────────────────────────────────────────────────────────── */}
      <Card className="pjr-section-card orange mb-3">
        <Card.Header className="py-2 d-flex justify-content-between align-items-center">
          <span className="fw-semibold fs-9 text-uppercase">Tool Run Data</span>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={addRun}
            disabled={reportData.runs.length >= 5}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add Run
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {reportData.runs.map((run, idx) => {
            const runInterval = calcTotalInterval(
              run.urDepthIn,
              run.urDepthOut
            );
            const runROP = calcAvgROP(runInterval, run.totHoursDrilling);
            return (
              <div
                key={idx}
                className={idx > 0 ? 'border-top pt-3 px-3 pb-3' : 'p-3'}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-bold fs-9 text-primary">
                    Run #{run.runNumber}
                  </span>
                  {reportData.runs.length > 1 && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => removeRun(idx)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  )}
                </div>
                <Row className="g-2">
                  <Col md={3}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Serial Number
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="e.g. DTU-13-22-2"
                      value={run.serialNumber}
                      onChange={e =>
                        updateRun(idx, 'serialNumber', e.target.value)
                      }
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Depth In (ft)
                    </Form.Label>
                    {numField(idx, 'urDepthIn')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Depth Out (ft)
                    </Form.Label>
                    {numField(idx, 'urDepthOut')}
                  </Col>
                  <Col md={1}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Interval
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      className="pjr-calc-field"
                      readOnly
                      value={
                        runInterval !== null ? runInterval.toFixed(0) : '—'
                      }
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Hrs on DC
                    </Form.Label>
                    {numField(idx, 'totHoursDC', '0', '0.5')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Hrs Drilling
                    </Form.Label>
                    {numField(idx, 'totHoursDrilling', '0', '0.5')}
                  </Col>
                  <Col md={1}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      ROP
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      className="pjr-calc-field"
                      readOnly
                      value={runROP !== null ? runROP.toFixed(1) : '—'}
                    />
                  </Col>
                </Row>
                <Row className="g-2 mt-1">
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      WOB Lo (klbs)
                    </Form.Label>
                    {numField(idx, 'wobLo')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      WOB Hi (klbs)
                    </Form.Label>
                    {numField(idx, 'wobHi')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      RPM Lo
                    </Form.Label>
                    {numField(idx, 'rpmLo')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      RPM Hi
                    </Form.Label>
                    {numField(idx, 'rpmHi')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Torque Lo (A)
                    </Form.Label>
                    {numField(idx, 'torqueLo')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Torque Hi (A)
                    </Form.Label>
                    {numField(idx, 'torqueHi')}
                  </Col>
                </Row>
                <Row className="g-2 mt-1">
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      SPM
                    </Form.Label>
                    {numField(idx, 'spm')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Flow Rate (gpm)
                    </Form.Label>
                    {numField(idx, 'flowRate')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Mud Wt (ppg)
                    </Form.Label>
                    {numField(idx, 'mudWeight', '8.6', '0.1')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Viscosity (Pa.s)
                    </Form.Label>
                    {numField(idx, 'viscosity')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Pump PSI
                    </Form.Label>
                    {numField(idx, 'pumpPressure')}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      PSI Across UR
                    </Form.Label>
                    {numField(idx, 'psiAcrossUR')}
                  </Col>
                  <Col md={3}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Formation Type
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="e.g. GUMBO"
                      value={run.formationType}
                      onChange={e =>
                        updateRun(idx, 'formationType', e.target.value)
                      }
                    />
                  </Col>
                </Row>
              </div>
            );
          })}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Step2DrillingParams;
